import { useSelector, useDispatch } from 'react-redux';
import { shallowEqual } from 'react-redux';
import { DesignSystemGalleryItem } from 'designSystem/types';
import { RootReducer } from 'redux/reducers';
import useScrollMemory from 'hooks/useScrollMemory';
import GalleryPreview from '../GalleryPreview';
import {
	addDsTab,
	removeDsTab,
	removeDsPick,
	setActiveDsTab,
	toggleDsPick,
} from 'redux/mode/actions';
import { displayConfirmModal } from 'redux/mode/util';
import { DS_TAB_CHROME } from 'designSystem/config';
import forge from 'apis/forge';
import { designSystemEditingBlocked } from 'redux/db/util';
import Icon, { IconType } from 'components/Icon';
import * as Base from '../../styles';
import * as S from './styles';

interface GalleryPageProps {
	galleryEntry: DesignSystemGalleryItem;
	rawCode: {
		theme: string;
		styles: string;
		componentsCss: string;
		componentsJs: string;
	};
	showBackButton?: boolean;
}

const GalleryPage = ({ galleryEntry, rawCode, showBackButton }: GalleryPageProps) => {
	const dispatch = useDispatch();
	const tabId = `gallery-${galleryEntry.path}`;
	const scrollRef = useScrollMemory(tabId);
	const { dsTabs, selectedDsPicks, config, assets, activeDesignSystemId } = useSelector(
		(r: RootReducer) => ({
			dsTabs: r.mode.dsTabs,
			selectedDsPicks: r.mode.selectedDsPicks,
			config: r.mode.designSystemContent?.config,
			assets: r.mode.designSystemContent?.assets?.all || [],
			activeDesignSystemId: r.mode.activeDesignSystemId,
		}),
		shallowEqual
	);
	const isGallerySelected = selectedDsPicks.some(
		(p) => p.kind === 'gallery' && p.galleryPath === galleryEntry.path
	);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const onBackToList = () => {
		// Navigate to the main Gallery tab if it exists, otherwise create it
		const galleryTab = dsTabs.find((tab) => tab.section === 'gallery' && !tab.contentPath);
		if (galleryTab) {
			dispatch(setActiveDsTab(galleryTab.id));
		} else {
			// Create a new Gallery tab
			const newTabId = 'section-gallery';
			const chrome = DS_TAB_CHROME.gallery;
			dispatch(
				addDsTab({
					id: newTabId,
					section: 'gallery',
					label: chrome?.label || 'Gallery',
				})
			);
			dispatch(setActiveDsTab(newTabId));
		}
	};

	const onDelete = () => {
		if (!activeDesignSystemId || designSystemEditingBlocked()) return;
		const path = galleryEntry.path;
		const title = galleryEntry.title;
		displayConfirmModal({
			title: 'Delete gallery item',
			description: `Are you sure you want to delete "${title}"?`,
			submitLabel: 'Delete',
			onCancel: () => {},
			onSubmit: async () => {
				await forge.updateDsFiles({
					designSystemId: activeDesignSystemId,
					files: [{ path, content: '' }],
				});
				dispatch(removeDsTab(tabId));
				dispatch(removeDsPick({ kind: 'gallery', galleryPath: path }));
			},
		});
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	return (
		<S.Base ref={scrollRef}>
			<S.Header>
				<S.HeaderContent>
					{showBackButton && (
						<S.BackButton onClick={onBackToList}>
							<Icon type={IconType.LINE_ARROW_LEFT} width={18} height={18} />
						</S.BackButton>
					)}
					<S.Title>{galleryEntry.title}</S.Title>
					<S.HeaderActions>
						<Base.SelectToggle
							$selected={isGallerySelected}
							onClick={() =>
								dispatch(toggleDsPick({ kind: 'gallery', galleryPath: galleryEntry.path }))
							}
						>
							{isGallerySelected ? 'Added to Chat' : 'Add to Chat'}
						</Base.SelectToggle>
						{!designSystemEditingBlocked() && (
							<S.DeleteButton onClick={onDelete}>Delete</S.DeleteButton>
						)}
					</S.HeaderActions>
				</S.HeaderContent>
			</S.Header>

			<S.Content>
				<S.PreviewWrapper>
					<GalleryPreview
						storyHtml={galleryEntry.code}
						isValidHtml={galleryEntry.isValidHtml}
						rawCode={rawCode}
						config={config}
						assets={assets}
					/>
				</S.PreviewWrapper>
			</S.Content>
		</S.Base>
	);
};

export default GalleryPage;
